import { FC, MouseEvent, useCallback, useState, useEffect, useMemo, useContext } from 'react';
import { useFragment, graphql, useRelayEnvironment } from 'react-relay';
import { useDispatch } from 'react-redux';

import { FormattedMessage } from 'dibs-react-intl';
import { sessionStorage } from 'dibs-browser-storage';
import { Link } from 'dibs-elements/exports/Link';
import { isEmailOnly } from 'dibs-cookie-jar/exports/isEmailOnly';
import PasswordRequirements from 'dibs-buyer-layout/exports/PasswordRequirements';
import { Spinner } from 'dibs-elements/exports/Spinner';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';

import Email from './fields/Email';
import Password from './fields/Password';
import { useCheckoutSelector } from '../../reducers/root';
import { openLoginModal } from '../../actions/checkoutAuthActions';
import { setTriggeredErrors } from '../../actions/checkoutUIActions';
import { transactionBuyerChangedAction } from '../../actions/commonActions';
import { trackFormInteraction, trackFormError } from '../../helpers/checkoutTracking';
import { STORAGE } from '../../constants/checkoutConstants';
import { CheckoutServerVarsContext } from '../shared/CheckoutServerVarsContext';
import { CheckoutRegistrationTerms } from './CheckoutRegistrationTerms';
import { CheckoutRegistrationMarketingOptIn } from './CheckoutRegistrationMarketingOptIn';

import { CheckoutRegistrationForm_viewer$key } from './__generated__/CheckoutRegistrationForm_viewer.graphql';
import { CheckoutRegistrationForm_buyer$key } from './__generated__/CheckoutRegistrationForm_buyer.graphql';

import styles from './styles/checkoutRegistrationForm.scss';
import dibsCss from 'dibs-css';

const viewerFragment = graphql`
    fragment CheckoutRegistrationForm_viewer on Viewer {
        ...CheckoutRegistrationMarketingOptIn_viewer
    }
`;
const buyerFragment = graphql`
    fragment CheckoutRegistrationForm_buyer on User {
        authMetadata {
            hasPassword
        }
    }
`;

export const CheckoutRegistrationForm: FC<{
    viewer: CheckoutRegistrationForm_viewer$key | null;
    buyer: CheckoutRegistrationForm_buyer$key | null;
    errors: Record<string, $TSFixMe>;
    showErrors: boolean;
    form: Record<string, string>;
    onChange: ({ field, value }: { field: string; value: string }) => void;
}> = ({ viewer: viewerRef, buyer: buyerRef, errors, showErrors, form, onChange }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const buyer = useFragment(buyerFragment, buyerRef);
    const environment = useRelayEnvironment();

    const [isEmailBlurred, setIsEmailBlurred] = useState(false);
    const [isPasswordBlurred, setIsPasswordBlurred] = useState(false);
    const [isSsoLoaded, setIsSsoLoaded] = useState(false);
    const [ssoError, setSsoError] = useState<string>('');

    useEffect(() => {
        if (showErrors) {
            setIsEmailBlurred(true);
            setIsPasswordBlurred(true);
            setSsoError('');
        }
    }, [showErrors]);

    const dispatch = useDispatch();

    const onBlur = useCallback(
        ({ field, value }: { field: string; value: string }): void => {
            if (value) {
                const sessionData = sessionStorage.getItem(STORAGE.AUTO_MODAL_INFO);
                const autoModalInfo = typeof sessionData === 'object' ? sessionData : {};

                if (field === 'email' && autoModalInfo) {
                    sessionStorage.setItem(STORAGE.AUTO_MODAL_INFO, {
                        ...autoModalInfo,
                        email: errors.email ? '' : value,
                    });
                }
                if (errors[field]) {
                    trackFormError({
                        category: 'registration',
                        step: 'registration form',
                        error: errors[field],
                    });
                    dispatch(setTriggeredErrors([field]));
                }
            }

            if (field === 'email') {
                setIsEmailBlurred(true);
            } else if (field === 'password') {
                setIsPasswordBlurred(true);
            }

            //clean sso error if there is one after user intreacts with the registration form
            setSsoError('');
        },
        [errors, dispatch]
    );

    const onLogin = useCallback(
        (event: MouseEvent): void => {
            event.preventDefault();
            trackFormInteraction();
            openLoginModal({ email: null, dispatch });
        },
        [dispatch]
    );

    const hasSetUpCheckout = useCheckoutSelector(state => state.ui.hasSetUpCheckout);
    const isAuthChecked = useCheckoutSelector(state => state.ui.authState === 'CHECKED');

    const ssoContent = useMemo(() => {
        if (!hasSetUpCheckout || !isAuthChecked) {
            return null;
        }

        return authModalLoader.loadAuthContent({
            action: authModalLoader.constants.SSO,
            flow: authModalLoader.constants.CHECKOUT_SSO_FLOW,
            ga: {
                label: 'checkout',
            },
            onAuthComplete: (data: $TSFixMe) => {
                if (data?.userId) {
                    dispatch(
                        transactionBuyerChangedAction({
                            buyerId: data.userId,
                        })
                    );
                }
            },
            onSsoLoad: ({ serverError, clearServerError = () => {} }: $TSFixMe = {}) => {
                setIsSsoLoaded(true);
                if (serverError) {
                    setSsoError(serverError);
                    clearServerError();
                }
            },
        });
    }, [dispatch, hasSetUpCheckout, isAuthChecked]);

    const userIsFullAccount = buyer?.authMetadata?.hasPassword;
    const { isMobile } = useContext(CheckoutServerVarsContext);

    const authContent = (
        <>
            {isEmailOnly(document.cookie) ? (
                <div className={styles.email} data-tn="register-prepopulated-email">
                    {form.email}
                </div>
            ) : (
                <Email
                    value={form.email}
                    error={errors.email}
                    isBlurred={isEmailBlurred}
                    onChange={onChange}
                    onBlur={onBlur}
                    environment={environment}
                />
            )}

            <Password
                value={form.password}
                error={
                    isEmailOnly(document.cookie) ? errors.email || errors.password : errors.password
                }
                isBlurred={isPasswordBlurred}
                onChange={onChange}
                onBlur={onBlur}
                isFullAccount={userIsFullAccount}
            />

            {isPasswordBlurred && (
                <div className={styles.passwordRequirementsWrapper}>
                    <PasswordRequirements errorCodes={errors.password} />
                </div>
            )}
        </>
    );

    return (
        <div>
            <p className={styles.topNotice}>
                <FormattedMessage
                    id="checkout.CheckoutRegistrationForm.track"
                    defaultMessage="Receive shipping updates and get full site access when you register on 1stDibs. Have an account? <a>Log in here</a>."
                    values={{
                        a: content => <Link onClick={onLogin}>{content}</Link>,
                    }}
                />
            </p>

            <div className={styles.form}>
                <div className={styles.sso}>
                    {/**
                     * We need to use CSS to hide SSO content because we need to memoize
                     * authModalLoader.loadAuthContent response to prevent firing complicated
                     * calculations on every rerender.
                     */}
                    <div style={{ display: isSsoLoaded ? 'block' : 'none' }}>{ssoContent}</div>
                    {!isSsoLoaded && <Spinner containerClass={styles.ssoSpinner} />}
                </div>

                {ssoError && isMobile && <div className={styles.ssoError}>{ssoError}</div>}

                <div className={styles.delimiter}>
                    <div className={styles.delimiterText}>
                        <FormattedMessage
                            id="checkout.CheckoutRegistrationForm.delimiterMessage"
                            defaultMessage="or"
                        />
                    </div>
                </div>

                <div className={styles.auth}>{authContent}</div>
            </div>

            {ssoError && !isMobile && <div className={styles.ssoError}>{ssoError}</div>}

            <div className={`${dibsCss.mtXsmall}`}>
                <CheckoutRegistrationMarketingOptIn viewer={viewer} />
            </div>
            <CheckoutRegistrationTerms />
        </div>
    );
};
